import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Check, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { AuthImg } from '@/components/construction/AuthImg';

interface Stage {
  stageDefId: string;
  code: string;
  nameEn: string;
  parentCode: string | null;
  weightPct: number;
  sortOrder: number;
  isLeaf: boolean;
  isDone: boolean;
  completedAt: string | null;
}

interface VillaDetail {
  id: string;
  villaName: string;
  villaNumber: number;
  model: string;
  blockNumber: number | null;
  rate: number | string;
  progressPct: number;
  revenue: number;
  stages: Stage[];
}

const fmtSAR = (n: number) => `SAR ${Math.round(n).toLocaleString('en-US')}`;
const fmtDate = (s: string | null) => s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

export default function VillaDetailPage() {
  const { projectId, villaId } = useParams<{ projectId: string; villaId: string }>();
  const [villa, setVilla] = useState<VillaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<{ id: string; photoUrl: string; caption: string; takenAt: string; stageDef: { code: string } | null }[]>([]);

  const refresh = useCallback(async () => {
    if (!projectId || !villaId) return;
    try {
      const r = await apiClient.get<VillaDetail>(`/api/v1/construction/projects/${projectId}/villas/${villaId}`);
      setVilla(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, villaId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!projectId || !villaId) return;
    const url = '/api/v1/construction/projects/' + projectId + '/performance-photos';
    apiClient
      .get<{ id: string; photoUrl: string; caption: string; takenAt: string; villaId: string | null; stageDef: { code: string } | null }[]>(url)
      .then((r) => setPhotos(r.data.filter((p) => p.villaId === villaId).slice(0, 12)))
      .catch(() => setPhotos([]));
  }, [projectId, villaId]);

  async function toggle(stage: Stage) {
    if (!projectId || !villaId || !stage.isLeaf) return;
    setTogglingId(stage.stageDefId);
    try {
      await apiClient.put(
        `/api/v1/construction/projects/${projectId}/villas/${villaId}/stages/${stage.stageDefId}`,
        { isDone: !stage.isDone },
      );
      await refresh();
    } catch (err) {
      window.alert(`Could not update stage: ${extractErrorMessage(err)}`);
    } finally {
      setTogglingId(null);
    }
  }

  // Group stages by parent: top-level (parentCode === null) ordered by sortOrder,
  // children for each parent filtered by parentCode === parent.code
  const tree = useMemo(() => {
    if (!villa) return [];
    const sorted = [...villa.stages].sort((a, b) => a.sortOrder - b.sortOrder);
    const tops = sorted.filter((s) => s.parentCode === null);
    return tops.map((top) => ({
      stage: top,
      children: top.isLeaf ? [] : sorted.filter((s) => s.parentCode === top.code),
    }));
  }, [villa]);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  if (error || !villa) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-center text-sm text-muted-foreground">{error ?? 'Villa not found'}</div>
      <Link to={`/dashboard/construction`}>
        <Button variant="outline" size="sm">Back to construction</Button>
      </Link>
    </div>
  );

  const villaRate = Number(villa.rate);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/dashboard/construction">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </Link>
          <div className="flex flex-col items-end">
            <div className="text-base font-medium">{villa.villaName}</div>
            <div className="text-xs text-muted-foreground">{villa.model}{villa.blockNumber != null ? ` · B${villa.blockNumber}` : ''}</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Progress hero */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-sm text-muted-foreground uppercase tracking-wider">Overall progress</div>
            <div className="text-2xl font-display">{villa.progressPct.toFixed(1)}%</div>
          </div>
          <div className="h-3 bg-muted rounded overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${villa.progressPct >= 100 ? 'bg-emerald-600' : villa.progressPct > 0 ? 'bg-amber-500' : 'bg-muted-foreground/20'}`}
              style={{ width: `${villa.progressPct}%` }}
            />
          </div>
          <div className="flex items-baseline justify-between text-sm">
            <div className="text-muted-foreground">Revenue earned</div>
            <div><span className="font-medium">{fmtSAR(villa.revenue)}</span> <span className="text-muted-foreground text-xs">of {fmtSAR(villaRate)}</span></div>
          </div>
        </div>

        {/* Stage tree */}
        <div className="space-y-3">
          {tree.map(({ stage: parent, children }) => {
            const isParentGroup = !parent.isLeaf;
            const groupDoneWeight = isParentGroup
              ? children.reduce((sum, c) => sum + (c.isDone ? c.weightPct : 0), 0)
              : 0;
            const groupCompletePct = isParentGroup && parent.weightPct > 0
              ? Math.round((groupDoneWeight / parent.weightPct) * 100)
              : (parent.isDone ? 100 : 0);

            return (
              <div key={parent.stageDefId} className="border border-border rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-muted/40 border-b border-border">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{parent.code}</span>
                        <span className="truncate">{parent.nameEn}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Group weight {parent.weightPct}% · {fmtSAR(villaRate * parent.weightPct / 100)}
                      </div>
                    </div>
                    <div className="text-right text-xs">
                      <div className="font-medium">{groupCompletePct}%</div>
                      {isParentGroup && (
                        <div className="text-muted-foreground">{groupDoneWeight}/{parent.weightPct}%</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Leaf parent rendered as single row */}
                {parent.isLeaf && (
                  <StageRow
                    key={parent.stageDefId}
                    stage={parent}
                    villaRate={villaRate}
                    busy={togglingId === parent.stageDefId}
                    onToggle={() => toggle(parent)}
                  />
                )}

                {/* Sub-stage rows */}
                {!parent.isLeaf && children.map((c) => (
                  <StageRow
                    key={c.stageDefId}
                    stage={c}
                    villaRate={villaRate}
                    busy={togglingId === c.stageDefId}
                    onToggle={() => toggle(c)}
                  />
                ))}
              </div>
            );
          })}
        </div>

        {photos.length > 0 && (
          <section className="space-y-2 pt-2">
            <div className="text-sm font-medium">Work photos</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {photos.map((p) => (
                <div key={p.id} className="aspect-square rounded-md overflow-hidden border border-border bg-muted">
                  <AuthImg src={p.photoUrl} alt={p.caption} className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

interface StageRowProps {
  stage: Stage;
  villaRate: number;
  busy: boolean;
  onToggle: () => void;
}

function StageRow({ stage, villaRate, busy, onToggle }: StageRowProps) {
  const sar = villaRate * stage.weightPct / 100;
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-3 border-t border-border first:border-t-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm flex items-center gap-2">
          <span className="font-mono text-xs text-muted-foreground">{stage.code}</span>
          <span className="truncate">{stage.nameEn}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {stage.weightPct}% · {fmtSAR(sar)}
          {stage.isDone && stage.completedAt && (
            <span> · completed {fmtDate(stage.completedAt)}</span>
          )}
        </div>
      </div>
      {stage.isDone ? (
        <Button
          variant="outline"
          size="sm"
          onClick={onToggle}
          disabled={busy}
          className="gap-1.5 border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950/50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Done
        </Button>
      ) : (
        <Button
          variant="default"
          size="sm"
          onClick={onToggle}
          disabled={busy}
          className="gap-1.5"
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Mark done
        </Button>
      )}
    </div>
  );
}
